import {useState, useEffect} from 'react';
import {
    ResponsiveContainer,
    Sector,
    Pie,
    Cell,
    PieChart,
} from 'recharts';
import Rating from '@mui/material/Rating';
import { styled } from '@mui/material/styles';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { ReactComponent as Info } from '@Assets/info.svg';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import LensIcon from '@mui/icons-material/Lens';
import { Emoji } from 'emoji-picker-react';
import Tooltip from '@mui/material/Tooltip';
import { MdPieChart as PieChartIcon, MdStar as Star } from 'react-icons/md';
import { MdCheckBox as CheckBox, MdRadioButtonChecked as RadioButton } from 'react-icons/md';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './FieldCharts.scss';

export default function FieldCharts(props) {

    const [formFeedbackAnalysis, setFormFeedbackAnalysis] = useState({});
    const [fieldId, setFieldId] = useState(0);
    const [field, setField] = useState({});
    const [pieData, setPieData] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const className = 'ticketDashboards__fieldCharts';

    const HeartStyledRating = styled(Rating)({
        '& .MuiRating-iconFilled': {
            color: '#ff6d75',
        },
        '& .MuiRating-iconHover': {
            color: '#ff3d47',
        },
    });

    const CircleStyledRating = styled(Rating)({
        '& .MuiRating-iconFilled': {
            color: '#2784B9d6',
        },
        '& .MuiRating-iconHover': {
            color: '#2784B9',
        },
    });

    useEffect(() => {
        setFormFeedbackAnalysis(props.formFeedbackAnalysis);
        setFieldId(props.fieldId);
        let fieldObj = props?.formFeedbackAnalysis[props?.fieldId];
        setField(fieldObj?.field);
        if(fieldObj?.field?.type === 'multiChoice') {
            let choiceObj = {};
            let pieDataObj = [];
            for(var i = 0; i < JSON.parse(fieldObj?.field?.options)?.length; i++) {
                choiceObj = {
                    name: JSON.parse(fieldObj?.field?.options)[i]?.title,
                    value: fieldObj?.stats?.find((stat) => stat.choice === JSON.parse(fieldObj?.field?.options)[i]?.id)?.total ?? 0,
                    color: JSON.parse(fieldObj?.field?.options)[i]?.color
                }
                pieDataObj.push(choiceObj);
                setChartData(pieDataObj.filter(entry => entry.value > 0));
            }
            setPieData(pieDataObj);
        } else if(fieldObj?.field?.type === 'multiSelect') {
            let choiceObj = {};
            let pieDataObj = [];
            for(var i = 0; i < JSON.parse(fieldObj?.field?.options)?.length; i++) {
                choiceObj = {
                    name: JSON.parse(fieldObj?.field?.options)[i]?.title,
                    value: fieldObj?.stats?.find((stat) => stat.selectChoice === JSON.parse(fieldObj?.field?.options)[i]?.id)?.total ?? 0,
                    color: JSON.parse(fieldObj?.field?.options)[i]?.color
                }
                pieDataObj.push(choiceObj);
                setChartData(pieDataObj.filter(entry => entry.value > 0));
            }
            setPieData(pieDataObj);
        }
    }, [props])

    const RADIAN = Math.PI / 180;

    // The value labels are drawn outside the pie, so the pie is sized to whatever is left after
    // reserving room for them (leader line + number) instead of a fixed radius that pushed the
    // labels out of narrow cards.
    const PIE_MARGIN = { top: 50, right: 70, bottom: 50, left: 70 };

    const renderCustomizedLabel = ({
        cx, cy, midAngle, outerRadius, name, value, fill
    }) => {
        const radius = outerRadius + 12;
        const sin = Math.sin(-RADIAN * midAngle);
        const cos = Math.cos(-RADIAN * midAngle);

        const startX = cx + radius * cos;
        const startY = cy + radius * sin;
        const midX = startX + (cos >= 0 ? 8 : -8);
        const midY = startY;
        const endX = midX + (cos >= 0 ? 14 : -14);
        const endY = midY;
        const valueGap = cos >= 0 ? 6 : -6;

        return (
            <g>
                <path
                    d={`M${cx + outerRadius * cos},${cy + outerRadius * sin} L${startX},${startY} Q${midX},${midY} ${endX},${endY}`}
                    fill="none"
                    stroke={fill}
                    strokeWidth={2}
                />
                <text
                    x={endX + valueGap}
                    y={endY}
                    fill={fill}
                    fontWeight="bold"
                    fontSize={12}
                    textAnchor={cos >= 0 ? 'start' : 'end'}
                    dominantBaseline="middle"
                >
                    {value}
                </text>
            </g>
        );
    };

    const renderActiveShape = ({ cx, cy, startAngle, endAngle, outerRadius, fill }) => (
        <g style={{ filter: 'drop-shadow(5px 5px 2.5px var(--color-shadow))' }}>
            <Sector
                cx={cx}
                cy={cy}
                innerRadius={0}
                outerRadius={outerRadius}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
            />
        </g>
    );

    const CustomPieTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip" style={{
                    backgroundColor: 'var(--color-muiBackground)',
                    border: 'solid 1px var(--color-darkBorder)',
                    padding: '0.5rem',
                    borderRadius: '0.5rem',
                    fontSize: '1.1rem',
                    boxShadow: '5px 5px 10px -5px var(--color-shadow)'
                }}>
                    <p><b>{payload[0].name}</b></p>
                    <p>Value: {payload[0].value}</p>
                </div>
            );
        }

        return null;
    };

    return (
        <div className={`${className}`}>
            <div className={`${className}__card`}>
                {/* Header */}
                <div className={`${className}__header`}>
                    <div className={`${className}__header__left`}>
                        {field?.type === 'rating' && <Star className={`${className}__header__icon`} />}
                        {field?.type === 'multiChoice' && <RadioButton className={`${className}__header__icon`} />}
                        {field?.type === 'multiSelect' && <CheckBox className={`${className}__header__icon`} />}
                        <h3 className={`${className}__header__title`}>{field?.title}</h3>
                    </div>
                    {field?.type !== 'rating' && (
                        <Tooltip
                            title={
                                <div className={`${className}__tooltip`}>
                                    {pieData?.map((entry, index) => (
                                        <div key={index} className={`${className}__tooltip__item`}>
                                            <div className={`${className}__tooltip__item__left`}>
                                                <div 
                                                    className={`${className}__tooltip__item__indicator`} 
                                                    style={{backgroundColor: entry?.color}}
                                                />
                                                <span className={`${className}__tooltip__item__label`}>
                                                    {entry?.name}
                                                </span>
                                            </div>
                                            <span className={`${className}__tooltip__item__value`}>
                                                {entry?.value}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            }
                            arrow
                            placement="top"
                        >
                            <div className={`${className}__header__info`}>
                                <Info className={`${className}__header__info__icon`} />
                            </div>
                        </Tooltip>
                    )}
                </div>

                {/* Content */}
                <div className={`${className}__content`}>
                    {/* Rating Type */}
                    {field?.type === 'rating' && (
                        <div className={`${className}__rating`}>
                            {/* Scale Rating */}
                            {JSON.parse(field?.rules)?.find((rule) => rule.type === 'ratingType')?.value === 'scale' && (
                                <div className={`${className}__rating__scale`}>
                                    <div className={`${className}__rating__scale__display`}>
                                        {JSON.parse(formFeedbackAnalysis[fieldId].field?.rules)?.find((rule) => rule.type === 'scaleIcon')?.value === 'star' && (
                                            <Rating
                                                value={formFeedbackAnalysis[fieldId].stats?.avgValue}
                                                max={Number(JSON.parse(formFeedbackAnalysis[fieldId].field?.rules)?.find((rule) => rule.type === 'width')?.value)}
                                                size='large'
                                                readOnly
                                            />
                                        )}
                                        {JSON.parse(field?.rules)?.find((rule) => rule.type === 'scaleIcon')?.value === 'heart' && (
                                            <HeartStyledRating
                                                value={formFeedbackAnalysis[fieldId]?.stats?.avgValue}
                                                max={Number(JSON.parse(formFeedbackAnalysis[fieldId].field?.rules)?.find((rule) => rule.type === 'width')?.value)}
                                                icon={<FavoriteIcon fontSize='inherit' />}
                                                size='large'
                                                readOnly
                                                emptyIcon={<FavoriteBorderIcon fontSize='inherit' />}
                                            />
                                        )}
                                        {JSON.parse(field?.rules)?.find((rule) => rule.type === 'scaleIcon')?.value === 'circle' && (
                                            <CircleStyledRating
                                                value={formFeedbackAnalysis[fieldId]?.stats?.avgValue}
                                                max={Number(JSON.parse(formFeedbackAnalysis[fieldId].field?.rules)?.find((rule) => rule.type === 'width')?.value)}
                                                icon={<LensIcon fontSize='inherit' />}
                                                size='large'
                                                readOnly
                                                emptyIcon={<RadioButtonUncheckedIcon fontSize='inherit' />}
                                            />
                                        )}
                                    </div>
                                    <div className={`${className}__rating__scale__value`}>
                                        <span className={`${className}__rating__scale__value__label`}>Average</span>
                                        <span className={`${className}__rating__scale__value__number`}>
                                            {formFeedbackAnalysis[fieldId]?.stats?.avgValue?.toFixed(2) ?? '0.00'}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Range Rating */}
                            {JSON.parse(field?.rules)?.find((rule) => rule.type === 'ratingType')?.value === 'range' && (
                                <div className={`${className}__rating__range`}>
                                    <div className={`${className}__rating__range__labels`}>
                                        <span className={`${className}__rating__range__labels__initial`}>
                                            {JSON.parse(field?.rules)?.find((rule) => rule.type === 'ratingInitialLabel')?.value}
                                        </span>
                                        <span className={`${className}__rating__range__labels__final`}>
                                            {JSON.parse(field?.rules)?.find((rule) => rule.type === 'ratingFinalLabel')?.value}
                                        </span>
                                    </div>
                                    <div className={`${className}__rating__range__display`}>
                                        <Rating
                                            value={formFeedbackAnalysis[fieldId].stats?.avgValue}
                                            max={Number(JSON.parse(formFeedbackAnalysis[fieldId].field?.rules)?.find((rule) => rule.type === 'width')?.value)}
                                            size='large'
                                            readOnly
                                        />
                                    </div>
                                    <div className={`${className}__rating__range__value`}>
                                        <span className={`${className}__rating__range__value__label`}>Average</span>
                                        <span className={`${className}__rating__range__value__number`}>
                                            {formFeedbackAnalysis[fieldId]?.stats?.avgValue?.toFixed(2) ?? '0.00'}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Select Rating */}
                            {JSON.parse(field?.rules)?.find((rule) => rule.type === 'ratingType')?.value === 'select' && (
                                <div className={`${className}__rating__select`}>
                                    <div className={`${className}__rating__select__options`}>
                                        <FormControl>
                                            <RadioGroup row>
                                                {JSON.parse(field?.options)?.map((option, index) => (
                                                    <FormControlLabel 
                                                        key={index}
                                                        value={option.value} 
                                                        labelPlacement='top'
                                                        control={
                                                            <Radio
                                                                checked={Number(option.value) === Number(formFeedbackAnalysis[fieldId].stats?.avgValue?.toFixed(0))}
                                                                disabled={true}
                                                            />
                                                        }
                                                        label={
                                                            <div className={`${className}__rating__select__label`}>
                                                                {option.iconType === 'text' ? (
                                                                    option.title
                                                                ) : (
                                                                    <Emoji unified={option.iconValue.unified} size={25}/>
                                                                )}
                                                            </div>
                                                        } 
                                                    />
                                                ))}
                                            </RadioGroup>
                                        </FormControl>
                                    </div>
                                    <div className={`${className}__rating__select__value`}>
                                        <span className={`${className}__rating__select__value__label`}>Average</span>
                                        <span className={`${className}__rating__select__value__number`}>
                                            {formFeedbackAnalysis[fieldId]?.stats?.avgValue?.toFixed(2) ?? '0.00'}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Multi-Select Type */}
                    {field?.type === 'multiSelect' && chartData.length > 0 && (
                        <div className={`${className}__chart`}>
                            <ResponsiveContainer width='100%' height={300}>
                                <PieChart margin={PIE_MARGIN}>
                                    <Tooltip content={<CustomPieTooltip />} />
                                    <Pie
                                        data={chartData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius="100%"
                                        activeIndex={activeIndex}
                                        activeShape={renderActiveShape}
                                        label={renderCustomizedLabel}
                                        labelLine={false}
                                        onMouseEnter={(_, index) => setActiveIndex(index)}
                                        onMouseLeave={() => setActiveIndex(null)}
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={entry.color}
                                                stroke="none"
                                                opacity={activeIndex !== null && activeIndex !== index ? 0.2 : 1}
                                            />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* Multi-Choice Type */}
                    {field?.type === 'multiChoice' && chartData.length > 0 && (
                        <div className={`${className}__chart`}>
                            <ResponsiveContainer width='100%' height={300}>
                                <PieChart margin={PIE_MARGIN}>
                                    <Tooltip content={<CustomPieTooltip />} />
                                    <Pie
                                        data={chartData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius="100%"
                                        activeIndex={activeIndex}
                                        activeShape={renderActiveShape}
                                        label={renderCustomizedLabel}
                                        labelLine={false}
                                        onMouseEnter={(_, index) => setActiveIndex(index)}
                                        onMouseLeave={() => setActiveIndex(null)}
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={entry.color}
                                                stroke="none"
                                                opacity={activeIndex !== null && activeIndex !== index ? 0.2 : 1}
                                            />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* Empty State */}
                    {(field?.type === 'multiSelect' || field?.type === 'multiChoice') && chartData.length === 0 && (
                        <div className={`${className}__empty`}>
                            <PieChartIcon className={`${className}__empty__icon`} />
                            <p className={`${className}__empty__text`}>No responses yet</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
